/**
 * "No Shipments Yet" — accounts on file we have never sold to.
 *
 * WHY THIS TAB EXISTS
 * -------------------
 * These accounts were reachable from nowhere. Today's Plan and My
 * Territories are built on the follow-up tracker, which INNER JOINs
 * depletions facts and buckets by days-since-last-depletion — an account
 * with no shipment has no such date and cannot appear. Manually Added
 * Accounts covers only the ones a person typed in.
 *
 * For one Florida rep that hid 163 of 672 accounts: roughly a quarter of
 * their territory, invisible. It also made our answer to "I'm in Vegas,
 * let me work these accounts" wrong — we were telling reps to add an
 * account that already existed, which is the duplicate the name search
 * exists to prevent.
 *
 * Rep-added accounts are included, and badged. Excluding them was the
 * first cut and it broke the very case this was built for: Nevada's
 * accounts turned out to be hand-added with no shipments, so they were
 * filtered out here AND filtered out of the tracker, and the two
 * surfaces contradicted each other. Provenance is a detail; the question
 * is "which accounts here have never shipped".
 *
 * The state filter is here for the same reason it is on the tracker: a
 * rep who is travelling needs to look somewhere other than home.
 */

import SearchIcon from '@mui/icons-material/Search';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import { Alert, Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useMemo, useState, type ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { useFieldStates, useNoShipmentAccounts, type FieldAccountSummary } from '../../api/field';
import { PremisesBadge } from '../../components/PremisesBadge';
import { PremiumPagination } from '../../components/PremiumPagination';
import { StateFilterSelect } from '../../components/StateFilterSelect';
import { useSectionExport } from '../../hooks/useSectionExport';
import {
  accountColumns,
  excelSheet,
  isoDate,
  premisesLabel,
  type ExcelWorkbookSpec,
} from '../../util/excel-export';

const FONT_INTER = '"Inter", "Avenir", -apple-system, BlinkMacSystemFont, sans-serif';
const INK = '#0f0a07';
const GOLD = '#bb8c3f';
const IVORY = 'rgba(255, 253, 250, 0.75)';
const MAX_WIDTH = 900;

interface Props {
  onLogVisit: (account: { accountId: number; accountName: string }) => void;
}

export function NoShipmentsTab({ onLogVisit }: Props) {
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const statesQuery = useFieldStates();
  const query = useNoShipmentAccounts(stateFilter);

  const territory = useMemo(
    () => statesQuery.data?.territory_states ?? [],
    [statesQuery.data?.territory_states],
  );
  const allStates = useMemo(
    () => statesQuery.data?.states ?? territory,
    [statesQuery.data?.states, territory],
  );

  const allItems = useMemo(() => query.data?.items ?? [], [query.data?.items]);
  const scopeLabel = stateFilter.length > 0 ? stateFilter.join(', ') : 'your territory';

  // 163 accounts in one territory is too many to scan. Name and city
  // both matter — a rep is either looking for a shop they remember or
  // for everything in the town they are standing in.
  const [search, setSearch] = useState('');
  const items = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allItems;
    return allItems.filter((a) =>
      [a.name, a.city, a.address].some((v) => v?.toLowerCase().includes(q)),
    );
  }, [allItems, search]);

  // Excel export: every matching account, not just the current page.
  // The endpoint returns the whole scope in one response, so `items`
  // already is the full filtered list.
  useSectionExport(
    (): ExcelWorkbookSpec => ({
      title: 'No Shipments Yet',
      filters: [
        {
          label: 'State',
          value:
            stateFilter.length > 0
              ? [...stateFilter].sort().join(', ')
              : territory.length > 0
                ? `Your territory (${territory.join(', ')})`
                : 'Your territory',
        },
        {
          label: 'Search (name, city or address)',
          value: search.trim() ? `"${search.trim()}"` : '',
        },
      ],
      sheets: [
        excelSheet({
          name: 'No Shipments Yet',
          rows: items,
          columns: [
            ...accountColumns<FieldAccountSummary>(),
            { header: 'Premises', value: (r) => premisesLabel(r.premises_type), width: 12 },
            {
              header: 'Source',
              value: (r) => (r.source === 'rep' ? 'Manually added' : 'Depletions feed'),
              width: 16,
            },
            { header: 'Added', value: (r) => isoDate(r.added_at), format: 'date' },
            { header: 'Added by', value: (r) => r.added_by_name, width: 18 },
            { header: 'Last visit', value: (r) => isoDate(r.last_visit_date), format: 'date' },
            {
              header: 'Last outcome',
              value: (r) => r.last_outcome?.replace(/_/g, ' ') ?? 'Never visited',
              width: 16,
            },
            { header: 'Last channel', value: (r) => r.last_channel, width: 10 },
            { header: 'Days since visit', value: (r) => r.days_since_last_visit, format: 'int' },
            { header: 'Pinned', value: (r) => (r.is_pinned ? 'Yes' : ''), width: 8 },
            { header: 'Last note', value: (r) => r.last_note_excerpt, width: 40 },
          ],
        }),
      ],
    }),
    !!query.data,
  );

  // Same control, same defaults as My Territories — a rep moving
  // between the two tabs should not have to learn a second one.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const pageCount = rowsPerPage === -1 ? 1 : Math.max(1, Math.ceil(items.length / rowsPerPage));
  // Filtering to a shorter list must not strand the rep on a page that
  // no longer exists.
  const safePage = Math.min(page, pageCount - 1);
  const visible =
    rowsPerPage === -1
      ? items
      : items.slice(safePage * rowsPerPage, safePage * rowsPerPage + rowsPerPage);

  return (
    <Stack gap={2} sx={{ maxWidth: MAX_WIDTH }}>
      {/* Context strip — mirrors the "Top picks" band on the other tabs
          so the surfaces read as one product, and answers the question a
          list of 163 rows otherwise leaves open: how many, and of what. */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
          p: 2,
          borderRadius: 2,
          border: `1px solid ${GOLD}2e`,
          bgcolor: IVORY,
        }}
      >
        <StorefrontOutlinedIcon sx={{ fontSize: 20, color: GOLD, mt: 0.25, flexShrink: 0 }} />
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
            <Typography sx={{ fontFamily: FONT_INTER, fontSize: 14, fontWeight: 700, color: GOLD }}>
              Never ordered
            </Typography>
            {!query.isPending && (
              <Typography
                sx={{ fontFamily: FONT_INTER, fontSize: 12.5, color: 'rgba(15,10,7,0.55)' }}
              >
                {allItems.length} account{allItems.length === 1 ? '' : 's'} in {scopeLabel}
                {search && items.length !== allItems.length ? ` · ${items.length} matching` : ''}
              </Typography>
            )}
          </Box>
          <Typography
            sx={{
              fontFamily: FONT_INTER,
              fontSize: 13,
              color: 'rgba(15,10,7,0.62)',
              mt: 0.4,
              lineHeight: 1.55,
            }}
          >
            On file, but no shipment has ever been reported — so they cannot appear in Today’s Plan
            or My Territories, which both rank by how long since the last one. Open one to start it.
          </Typography>
        </Box>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flex: 1,
            minWidth: 220,
            px: 1.5,
            height: 40,
            borderRadius: 2,
            bgcolor: '#fff',
            border: `1px solid ${GOLD}33`,
            transition: 'border-color 0.15s',
            '&:focus-within': { borderColor: GOLD },
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: 'rgba(15,10,7,0.4)' }} />
          <Box
            component="input"
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            placeholder="Search by name, city or address"
            aria-label="Search accounts without shipments"
            sx={{
              flex: 1,
              minWidth: 0,
              border: 'none',
              outline: 'none',
              bgcolor: 'transparent',
              fontFamily: FONT_INTER,
              fontSize: 13.5,
              color: INK,
              '&::placeholder': { color: 'rgba(15,10,7,0.4)', opacity: 1 },
            }}
          />
        </Box>
        <StateFilterSelect
          value={stateFilter}
          onChange={(next) => {
            setPage(0);
            setStateFilter(next);
          }}
          states={allStates}
          territoryStates={territory}
          emptyLabel="My territory"
          resetLabel="Back to my territory"
        />
      </Box>

      {query.isPending && (
        <Stack gap={1.5}>
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={92} />
          ))}
        </Stack>
      )}

      {query.isError && (
        <Alert severity="error" sx={{ fontFamily: FONT_INTER }}>
          Couldn’t load these accounts.
        </Alert>
      )}

      {!query.isPending && !query.isError && items.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 6,
            px: 2,
            border: '1px dashed rgba(187, 140, 63, 0.35)',
            borderRadius: 2,
            bgcolor: IVORY,
          }}
        >
          <StorefrontOutlinedIcon sx={{ fontSize: 34, color: 'rgba(187, 140, 63, 0.6)', mb: 1 }} />
          <Typography
            sx={{ fontFamily: FONT_INTER, fontSize: 14, fontWeight: 700, color: INK, mb: 0.5 }}
          >
            No accounts without shipments in {scopeLabel}
          </Typography>
          <Typography sx={{ fontFamily: FONT_INTER, fontSize: 13, color: 'rgba(15,10,7,0.6)' }}>
            {/* Deliberately not "everything here has shipped" — an empty
                list cannot tell the difference between that and there
                being no accounts on file at all. */}
            Every account we have here has shipped at least once, so they show up in Today’s Plan
            and My Territories instead.
          </Typography>
        </Box>
      )}

      {items.length > 0 && (
        <Box
          sx={{
            border: `1px solid ${GOLD}22`,
            borderRadius: 2,
            overflow: 'hidden',
            bgcolor: '#fff',
          }}
        >
          {visible.map((account: FieldAccountSummary, i: number) => (
            <Box
              key={account.account_id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.5,
                borderTop: i === 0 ? 'none' : `1px solid ${GOLD}18`,
                // A rail that appears on hover rather than a card border on
                // every row — 163 outlined boxes is a lot of chrome for a
                // list you scan.
                boxShadow: `inset 0 0 0 0 ${GOLD}`,
                transition: 'background-color 0.15s, box-shadow 0.15s',
                '&:hover': {
                  bgcolor: `${GOLD}0a`,
                  boxShadow: `inset 3px 0 0 0 ${GOLD}`,
                },
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Box
                    component="button"
                    type="button"
                    onClick={() => navigate(`/regional-sales/accounts/${account.account_id}`)}
                    sx={{
                      border: 'none',
                      bgcolor: 'transparent',
                      p: 0,
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontFamily: FONT_INTER,
                      fontSize: 14,
                      fontWeight: 700,
                      color: INK,
                      '&:hover': { color: GOLD },
                    }}
                  >
                    {account.name}
                  </Box>
                  <PremisesBadge premises={account.premises_type} />
                  {account.source === 'rep' && (
                    <Chip
                      label="Manually added"
                      size="small"
                      sx={{
                        height: 17,
                        fontSize: 9.5,
                        fontWeight: 700,
                        letterSpacing: '0.04em',
                        bgcolor: `${GOLD}1a`,
                        color: GOLD,
                      }}
                    />
                  )}
                </Box>
                <Typography
                  sx={{
                    fontFamily: FONT_INTER,
                    fontSize: 12.5,
                    color: 'rgba(15,10,7,0.55)',
                    mt: 0.15,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {[account.address, account.city, account.state_code].filter(Boolean).join(', ') ||
                    'No address on file'}
                  {account.last_visit_date
                    ? ` · last visit ${account.last_visit_date}${
                        account.last_outcome ? `, ${account.last_outcome.replace(/_/g, ' ')}` : ''
                      }`
                    : ' · never visited'}
                </Typography>
              </Box>

              <Box
                component="button"
                type="button"
                onClick={() =>
                  onLogVisit({ accountId: account.account_id, accountName: account.name })
                }
                sx={{
                  flexShrink: 0,
                  px: 1.75,
                  py: 0.7,
                  border: `1px solid ${GOLD}59`,
                  borderRadius: 999,
                  bgcolor: 'transparent',
                  color: GOLD,
                  fontFamily: FONT_INTER,
                  fontSize: 12.5,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  transition: 'background-color 0.15s, border-color 0.15s',
                  '&:hover': { bgcolor: `${GOLD}14`, borderColor: GOLD },
                }}
              >
                Log visit
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {items.length > 0 && (
        <PremiumPagination
          count={items.length}
          page={safePage}
          rowsPerPage={rowsPerPage}
          onPageChange={setPage}
          onRowsPerPageChange={(n) => {
            setRowsPerPage(n);
            setPage(0);
          }}
        />
      )}

      {/* Search that matched nothing is a different state from a scope
          with no such accounts at all. */}
      {!query.isPending && !query.isError && allItems.length > 0 && items.length === 0 && (
        <Typography
          sx={{ fontFamily: FONT_INTER, fontSize: 13, color: 'rgba(15,10,7,0.55)', py: 2 }}
        >
          Nothing matches “{search}”.
        </Typography>
      )}
    </Stack>
  );
}
